/**
 * The review-queue data hook. Fetches the open items (`GET /v1/review`, paginated
 * and server-sorted/filtered) together with the effective taxonomy
 * (`GET /v1/categories`) — the latter is what tells a *proposed new* suggestion
 * (the `NEW` chip) apart from an existing category. A sort/reason change re-keys
 * the effect and re-fetches, aborting any request in flight; `reload()` re-runs
 * the same load after a mutation.
 */
import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../../api/errors';
import type { EffectiveCategory, ReviewQueueItem } from '../../api/schema';

import { useReviewApi } from './reviewApi';

export interface QueueParams {
  sort: string;
  /** A `ReviewReason`, or `all`. */
  reason: string;
  /** 0-based page index. */
  page: number;
  /** Rows per page; one of {@link REVIEW_PAGE_SIZES}. */
  perPage: number;
}

export interface QueueData {
  items: ReviewQueueItem[];
  total: number;
  /** How many items the server skipped — the page's offset into `total`. */
  skip: number;
  categories: EffectiveCategory[];
  /** Names of every effective category, for the proposed-new derivation. */
  categoryNames: string[];
}

export type QueueState =
  | { status: 'loading' }
  | { status: 'error'; traceId: string | null; reload: () => void }
  | { status: 'ready'; data: QueueData; reload: () => void };

export function traceOf(error: unknown): string | null {
  if (error instanceof ApiError && error.problem !== null) {
    const problem = error.problem;
    const id = problem.correlation_id ?? problem.trace_id ?? problem.traceId;
    if (typeof id === 'string') return id;
  }
  return null;
}

function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * Rows-per-page choices, matching the Documents explorer so the two lists behave
 * the same way. Validated against on read, so a hand-edited URL cannot ask the
 * queue endpoint for an arbitrary page size.
 */
export const REVIEW_PAGE_SIZES: readonly number[] = [10, 25, 50, 100];

/** The default rows per page. */
export const REVIEW_PAGE_SIZE = 10;

export function useReviewQueue(params: QueueParams): QueueState {
  const api = useReviewApi();
  const [state, setState] = useState<QueueState>({ status: 'loading' });
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  const { sort, reason, page, perPage } = params;

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;
    setState({ status: 'loading' });

    void (async () => {
      try {
        const [result, categories] = await Promise.all([
          api.listQueue(
            {
              sort,
              reason: reason === 'all' ? undefined : reason,
              skip: page * perPage,
              limit: perPage,
            },
            signal,
          ),
          api.listCategories(signal),
        ]);
        if (signal.aborted) return;
        setState({
          status: 'ready',
          reload,
          data: {
            items: result.items,
            total: result.total,
            skip: result.skip,
            categories: categories.items,
            categoryNames: categories.items.map((c) => c.name),
          },
        });
      } catch (error) {
        if (signal.aborted || isAbort(error)) return;
        setState({ status: 'error', traceId: traceOf(error), reload });
      }
    })();

    return () => {
      controller.abort();
    };
  }, [api, sort, reason, page, perPage, nonce, reload]);

  return state;
}
